import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
 DialogActions,
  DialogContentText,
  Typography,
  IconButton,
  Box,
  Button,
  Divider,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from "react-i18next";
import "../i18n";

const ensureFullUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`;
};

// Format "YYYY/MM/DD" -> "6 October"
const formatDateHuman = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('/').map(Number);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${d} ${months[m - 1] || ''}`;
};

// Group meetings by date (to show like agenda sections)
const groupMeetingsByDate = (meetings = []) => {
  const groups = {};
  meetings.forEach((m) => {
    const key = m.date || 'Unknown date';
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  });
  return groups;
};

function TeacherUpcomingMeetingsModal({
  open,
  onClose,
  meetings = [],
  onDeleteMeeting, // <-- parent handles DB + state update
}) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  const handleJoin = (mtg) => {
    if (!mtg?.url) return;
    const fullUrl = ensureFullUrl(mtg.url);
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRequestDelete = (mtg) => {
    setSelectedMeeting(mtg);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedMeeting && onDeleteMeeting) {
      await onDeleteMeeting(selectedMeeting);
    }
    setConfirmOpen(false);
    setSelectedMeeting(null);
  };

  // Already sorted in parent, but we keep that order inside each date
  const grouped = groupMeetingsByDate(meetings);
  const dateKeys = Object.keys(grouped);

  const colors = ['#635BFF', '#FF8A00', '#FF3B30', '#34C759', '#0A84FF'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
        },
      }}
    >
      {/* Header with title + X icon */}
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0',
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('Your agenda')}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: '#fafbff',
        }}
      >
        {meetings.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
            }}
          >
            <Typography variant="body1" sx={{ color: '#6b7280' }}>
              {t("There are no upcoming classes at the moment.")}
            </Typography>
          </Box>
        ) : (
          dateKeys.map((dateKey) => (
            <Box key={dateKey} sx={{ mb: 1 }}>
              {/* Date heading */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  {formatDateHuman(dateKey)}
                </Typography>
              </Box>

              {/* Meetings for this date */}
              {grouped[dateKey].map((mtg, idx) => {
                const color = colors[idx % colors.length];
                return (
                  <Box
                    key={mtg.id}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 2,
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#ffffff',
                      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    {/* Delete icon (bin) */}
                    <IconButton
                      size="small"
                      onClick={() => handleRequestDelete(mtg)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#9ca3af',
                        '&:hover': { color: '#ef4444' },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                    {/* Time block */}
                    <Box sx={{ minWidth: 70 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: '#4c1d95', mb: 0.2 }}
                      >
                        {mtg.startTime || '--:--'}
                      </Typography>
                      {mtg.endTime && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#9ca3af' }}
                        >
                          {mtg.endTime}
                        </Typography>
                      )}
                    </Box>

                    {/* Color stripe */}
                    <Box
                      sx={{
                        width: 3,
                        borderRadius: 999,
                        background: color,
                        boxShadow: `0 0 10px ${color}55`,
                      }}
                    />

                    {/* Details + Join */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#6b7280' }}
                      >
                        {t("Live session")}
                      </Typography>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: '#111827',
                          mb: 0.2,
                        }}
                      >
                        {mtg.title || t("Untitled class")}
                      </Typography>

                      {mtg.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6b7280',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2, // show up to 2 lines
                            wordBreak: 'break-word',
                          }}
                          title={mtg.description}
                        >
                          {mtg.description}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleJoin(mtg)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 999,
                            px: 2.5,
                            py: 0.4,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            background:
                              'linear-gradient(135deg, #4f46e5, #7c3aed)',
                            boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                          }}
                        >
                          {t('Join')}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })}

              {/* Divider between dates */}
              <Divider sx={{ mt: 0.5 }} />
            </Box>
          ))
        )}
      </DialogContent>
      {/* Confirm delete dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>
          {t("Confirm class deletion")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center' }}>
            {t("Are you sure you want to delete this class from your agenda?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            color="error"
            variant="contained"
            fullWidth
            onClick={handleConfirmDelete}
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>

  );
}

export default TeacherUpcomingMeetingsModal;
